import set from 'lodash/set';
import { nanoid } from 'nanoid/non-secure';

import { getNowISO } from './helper';
import { safeSessionStorage, safeGetUserTraits } from './trackingStorage';
import { convert } from './v4';
import { identify } from './v4.calls';
import psProperties, { CONTACT_TRAIT_KEYS } from './v4.ps-properties';

// Prefix for storing campaign session IDs in sessionStorage
const CAMPAIGN_SESSION_PREFIX = 'perspective_campaign_session_';

// Tracks whether any non-homepage page has been seen in this session.
// Used to decide if a homepage arrival should regenerate the session.
const SEEN_NON_HOMEPAGE_PREFIX = 'perspective_seen_non_homepage_';

const conversionProperties = CONTACT_TRAIT_KEYS;

export class TrackingSession {
    private sessionId: string;
    private hasSeenAnyNonHomepage: boolean;
    private visitorProperties: Partial<Record<string, string>>;
    private lastIdentifyKey: string | null = null;

    /**
     * Whether this session may be written to sessionStorage.
     *
     * Defaults to on. `TrackingService` turns it off while the visitor has not answered the
     * cookie banner, so the visit is measured from memory alone and nothing lands on their
     * device — see `setPersistenceEnabled`.
     */
    private persistenceEnabled = true;

    constructor() {
        this.sessionId = '';
        this.hasSeenAnyNonHomepage = false;
        this.visitorProperties = {};
        this.lastIdentifyKey = null;
    }

    /**
     * Turn sessionStorage persistence on or off.
     *
     * While off the session lives only in this page's memory, so a reload cannot recognise it
     * and starts a new visit instead. Call `flushToStorage` when turning it back on to write
     * out whatever the visit has accumulated so far.
     */
    setPersistenceEnabled(enabled: boolean): void {
        this.persistenceEnabled = enabled;
    }

    /** Write the in-memory session out, for when persistence is enabled mid-visit. */
    flushToStorage(campaignId: string): void {
        if (!this.persistenceEnabled || !this.sessionId) return;

        safeSessionStorage.setItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`, this.sessionId);
        this.persistVisitorProperties();

        if (this.hasSeenAnyNonHomepage) {
            safeSessionStorage.setItem(`${SEEN_NON_HOMEPAGE_PREFIX}${this.sessionId}`, '1');
        }
    }

    static peek(campaignId: string): { sessionId: string; hasSeenAnyNonHomepage: boolean } | null {
        const sessionId = safeSessionStorage.getItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`);

        if (!sessionId) return null;

        const hasSeenAnyNonHomepage =
            safeSessionStorage.getItem(`${SEEN_NON_HOMEPAGE_PREFIX}${sessionId}`) === '1';

        return { sessionId, hasSeenAnyNonHomepage };
    }

    static peekBySessionId(sessionId: string): { hasSeenAnyNonHomepage: boolean } | null {
        const traits = safeGetUserTraits(`${sessionId}_user_traits`);

        if (traits === null) return null;

        const hasSeenAnyNonHomepage =
            safeSessionStorage.getItem(`${SEEN_NON_HOMEPAGE_PREFIX}${sessionId}`) === '1';

        return { hasSeenAnyNonHomepage };
    }

    private applyRestoredSession(sessionId: string, traits: string | null): void {
        this.sessionId = sessionId;
        this.hasSeenAnyNonHomepage =
            safeSessionStorage.getItem(`${SEEN_NON_HOMEPAGE_PREFIX}${sessionId}`) === '1';

        if (traits !== null) {
            try {
                const obj = JSON.parse(traits);

                for (const [key, val] of Object.entries(obj)) {
                    if (val !== null && val !== undefined) {
                        this.visitorProperties[key] = String(val);
                    }
                }
            } catch (error) {
                console.error('Failed to parse user traits:', error);
            }
        }

        // Expose for E2E testing — stripped from production builds by Vite
        if (import.meta.env.DEV && typeof window !== 'undefined') {
            (window as any).__visitorProperties = this.visitorProperties;
        }
    }

    tryRestore(campaignId: string): boolean {
        const sessionId = safeSessionStorage.getItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`);

        if (!sessionId) return false;

        const traits = safeGetUserTraits(`${sessionId}_user_traits`);
        this.applyRestoredSession(sessionId, traits);
        return true;
    }

    tryRestoreFromSessionId(campaignId: string, sessionId: string): boolean {
        const traits = safeGetUserTraits(`${sessionId}_user_traits`);

        if (traits === null) return false;

        this.applyRestoredSession(sessionId, traits);
        safeSessionStorage.setItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`, sessionId);
        return true;
    }

    create(campaignId: string): void {
        const urlSessionId = new URLSearchParams(window.location.search).get('sessionId');
        this.sessionId = urlSessionId || nanoid();

        if (this.persistenceEnabled) {
            safeSessionStorage.setItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`, this.sessionId);
            safeSessionStorage.setItem(`${this.sessionId}_user_traits`, '{}');
        }

        // Expose for E2E testing — stripped from production builds by Vite
        if (import.meta.env.DEV && typeof window !== 'undefined') {
            (window as any).__visitorProperties = this.visitorProperties;
        }
    }

    // --- Session ID ---

    getSessionId(): string {
        return this.sessionId;
    }

    /**
     * Whether this session should be regenerated on homepage arrival.
     * Returns true when the user has visited any non-homepage page in this session,
     * meaning a homepage arrival signals a new funnel journey.
     */
    shouldRegenerateForHomepage(): boolean {
        if (!this.sessionId) return false;
        return this.hasSeenAnyNonHomepage;
    }

    // Called from Page.svelte after variant resolution to track which page is currently shown.
    setCurrentPage(_pageSlug: string, isHomePage: boolean): void {
        if (!isHomePage && !this.hasSeenAnyNonHomepage) {
            this.hasSeenAnyNonHomepage = true;

            if (this.persistenceEnabled) {
                safeSessionStorage.setItem(`${SEEN_NON_HOMEPAGE_PREFIX}${this.sessionId}`, '1');
            }
        }
    }

    // --- Visitor Properties ---

    updateVisitorProperties(
        obj: { [key: string]: unknown },
        options?: { suppressConversion?: boolean },
    ): void {
        Object.keys(obj).forEach((key) => {
            set(this.visitorProperties, key, obj[key]);
        });

        set(this.visitorProperties, psProperties.lastSeenAt, getNowISO());

        // Apply conversion state BEFORE persisting/identifying so a single identify
        // (and the persisted traits) carry ps_converted.
        const justConverted = options?.suppressConversion ? false : this.applyConversionState();

        this.persistVisitorProperties();

        identify(this.visitorProperties);

        // Fire the Session Converted event AFTER identify so the user is identified first.
        if (justConverted) {
            convert();
        }
    }

    // Safety-net persistence of visitor properties to sessionStorage (CON-4201), so state
    // (including ps_converted) survives a reload even if Segment hasn't persisted it yet.
    private persistVisitorProperties(): void {
        if (!this.persistenceEnabled) return;

        if (this.sessionId) {
            safeSessionStorage.setItem(
                `${this.sessionId}_user_traits`,
                JSON.stringify(this.visitorProperties),
            );
        }
    }

    // Sets conversion fields if a conversion newly occurs this call.
    // Returns true when conversion was newly applied; does NOT fire the convert() track event.
    private applyConversionState(): boolean {
        if (this.visitorProperties[psProperties.isConverted]) {
            return false;
        }

        const hasConversionProp = conversionProperties.some(
            (property) => this.visitorProperties[property],
        );

        if (!hasConversionProp) {
            return false;
        }

        set(this.visitorProperties, psProperties.isConverted, true);
        set(this.visitorProperties, psProperties.convertedAt, getNowISO());
        return true;
    }

    triggerDeferredConversion(): void {
        const justConverted = this.applyConversionState();

        this.persistVisitorProperties();

        identify(this.visitorProperties);

        if (justConverted) {
            convert();
        }
    }

    getVisitorProperties(): Partial<Record<string, string>> {
        return this.visitorProperties;
    }

    // --- Identify Deduplication ---

    // Dedup on the stable identity payload only (traits + userId). analytics.js regenerates
    // messageId/timestamp on every call, so comparing the whole payload would never detect
    // two logically-identical identify calls as duplicates.
    isIdentifyDuplicate(data: { traits?: unknown; userId?: unknown }): boolean {
        const key = JSON.stringify({ traits: data.traits ?? null, userId: data.userId ?? null });
        const isEqual = key === this.lastIdentifyKey;

        if (!isEqual) {
            this.lastIdentifyKey = key;
        }
        return isEqual;
    }
}
